import { useStores } from '@littlebird/sdk';
import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { useIntegrationProviders } from '@/features/integration-management/integration-management.query';
import { pluginsStore } from '@/features/plugins/plugins.store';
import { integrationManagementSearchId } from '@/features/settings/settings-search-dom';
import { useHashRoute } from '@/hooks/useHashRoute';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { trackEvent } from '@/utils/analytics';

import { createAction, Section } from './actions';
import { type Action } from './actions/types';

const iconSize = 14;

export const useNavigationAction = () => {
  const router = useRouter();
  const { setHash } = useHashRoute();
  const { journals: journalStore } = useStores();
  const latestJournal = journalStore.orderedData[0];
  const { providers: installedIntegrations } = useIntegrationProviders();
  const hasFeature = useFeature();
  const isInternalUser = useIsInternalUser();

  const actions: Action[] = useMemo(
    () =>
      [
        hasFeature(FEATURES.ACTION_CARDS_FEED) && {
          id: 'feed',
          name: 'Feed',
          keywords: 'feed action cards to-dos todos',
          icon: <Icons.galleryVerticalEnd size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            router.navigate({ to: '/feed' });
          },
        },
        {
          id: 'chats',
          name: 'Chats',
          keywords: 'chats history browse list',
          icon: <Icons.chat size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            trackEvent('ALL_CHATS_SEE_ALL_CLICKED');
            router.navigate({ to: '/chats/all' });
          },
        },
        {
          id: 'routines',
          name: 'Routines',
          icon: <Icons.routines size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            router.navigate({ to: '/routines' });
          },
        },
        {
          id: 'meeting-notes',
          name: 'Meeting Notes',
          icon: <Icons.meetings size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            router.navigate({ to: '/meetings' });
          },
        },
        hasFeature(FEATURES.JOURNALS) &&
          latestJournal && {
            id: 'journals',
            name: 'Journals',
            icon: <Icons.journal size={iconSize} />,
            section: Section.Navigation,
            perform: () => {
              router.navigate({ to: '/journal' });
            },
          },
        {
          id: 'plugins',
          name: 'Integrations',
          icon: <Icons.playground size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            router.navigate({ to: '/integrations' });
          },
          children: installedIntegrations.map((integration) => ({
            id: `plugins.${integration.key}`,
            name: integration.name,
            section: Section.Navigation,
            perform: () =>
              setHash(
                `settings/Integrations/${integrationManagementSearchId(integration.key)}`
              ),
          })),
        },
        {
          id: 'skills',
          name: 'Skills',
          icon: <Icons.wandSparkles size={iconSize} />,
          section: Section.Navigation,
          perform: () => {
            pluginsStore.setActiveTab('skills');
            router.navigate({ to: '/integrations' });
          },
        },
        isInternalUser &&
          hasFeature(FEATURES.API_REQUEST_LOGGER) && {
            id: 'debug',
            name: 'Debug',
            icon: <Icons.debug size={iconSize} />,
            section: Section.Navigation,
            perform: () => {
              router.navigate({ to: '/debug' });
            },
          },
        isInternalUser &&
          hasFeature(FEATURES.API_REQUEST_LOGGER) && {
            id: 'meeting-debug',
            name: 'Meeting Debug',
            icon: <Icons.debug size={iconSize} />,
            section: Section.Navigation,
            perform: () => {
              router.navigate({ to: '/meeting-debug' });
            },
          },
      ].filter(Boolean) as Action[],
    [
      hasFeature,
      installedIntegrations,
      isInternalUser,
      latestJournal,
      router,
      setHash,
    ]
  );

  return useMemo(() => actions.map(createAction), [actions]);
};
